import { useState } from 'react';
import Modal from '../../components/Modal';
import { mapsApi, ApiError } from '../../services/api';
import { useEditor } from '../../contexts/EditorContext';
import { MapMeta } from '../../types/map';
import './DeleteMapModal.css';

interface DeleteMapModalProps {
  isOpen: boolean;
  onClose: () => void;
  map: MapMeta | null;
}

/**
 * Confirmation modal for deleting a map
 * @param isOpen - Controls modal visibility
 * @param onClose - Callback when modal is closed
 * @param map - The map to delete
 * @returns The rendered delete confirmation modal component
 */
function DeleteMapModal({ isOpen, onClose, map }: DeleteMapModalProps): JSX.Element {
  const { refreshMaps, selectMap } = useEditor();
  const [error, setError] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  /**
   * Handles map deletion
   */
  const handleDelete = async (): Promise<void> => {
    if (!map) return;

    setError('');
    setIsDeleting(true);

    try {
      await mapsApi.delete(map.id);
      selectMap(null);
      await refreshMaps();
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Failed to delete map');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Resets state and closes modal
   */
  const handleClose = (): void => {
    setError('');
    onClose();
  };

  if (!map) {
    return <></>;
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Delete Map">
      <div className="delete-map-content">
        {error && <div className="delete-map-error">{error}</div>}

        <p className="delete-map-message">
          Are you sure you want to delete <strong>{map.label}</strong>?
        </p>
        <p className="delete-map-warning">
          This action cannot be undone.
        </p>

        <div className="delete-map-actions">
          <button
            type="button"
            className="delete-map-cancel"
            onClick={handleClose}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="delete-map-confirm"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default DeleteMapModal;
